import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { handleRedirectCallback, loadSession, login, logout } from "./cognitoAuth";
import type { Session } from "./cognitoAuth";

interface AuthContextValue {
  isAuthenticated: boolean;
  idToken: string | null;
  email: string | null;
  /** Set while the Hosted UI redirect's code exchange is in flight. */
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleRedirectCallback()
      .then((result) => {
        if (result) setSession(result);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setIsLoading(false));
  }, []);

  const value: AuthContextValue = {
    isAuthenticated: session !== null,
    idToken: session?.idToken ?? null,
    email: session?.email ?? null,
    isLoading,
    error,
    login: () => {
      void login();
    },
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
